// frontend/src/components/common/Button.tsx
import React from 'react';
import { useTheme, ColorTheme } from '../../contexts/ThemeContext';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  customClasses?: string;
}

const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  loading = false,
  leftIcon,
  rightIcon,
  className = '',
  customClasses = '',
  disabled,
  ...rest
}) => {
  const { themeClasses, darkMode, colorTheme } = useTheme();

  const baseStyles = 'inline-flex items-center justify-center border font-medium rounded-md focus:outline-none transition duration-150 ease-in-out disabled:opacity-50';
  
  // Define size styles
  const sizeStyles = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base',
  };
  
  // Define width class
  const widthClass = fullWidth ? 'w-full' : '';
  
  // Get variant styles with hardcoded classes for all possible themes
  const getVariantStyles = () => {
    switch (variant) {
      case 'primary':
        // Use theme-specific primary color from ThemeContext
        return themeClasses.primaryButton;
        
      case 'secondary':
        // Use theme-specific secondary color from ThemeContext
        return `${themeClasses.secondaryButton} hover:${themeClasses.secondaryButtonHover}`;
        
      case 'outline':
        // Use hardcoded classes based on the current theme
        switch (colorTheme) {
          case 'rose':
            return 'border-rose-300 dark:border-rose-700 text-rose-700 dark:text-rose-300 bg-white dark:bg-gray-800 hover:bg-rose-50 dark:hover:bg-gray-700';
          case 'lavender':
            return 'border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-300 bg-white dark:bg-gray-800 hover:bg-purple-50 dark:hover:bg-gray-700';
          case 'mint':
            return 'border-green-300 dark:border-green-700 text-green-700 dark:text-green-300 bg-white dark:bg-gray-800 hover:bg-green-50 dark:hover:bg-gray-700';
          case 'peach':
            return 'border-orange-300 dark:border-orange-700 text-orange-700 dark:text-orange-300 bg-white dark:bg-gray-800 hover:bg-orange-50 dark:hover:bg-gray-700';
          case 'sky':
            return 'border-sky-300 dark:border-sky-700 text-sky-700 dark:text-sky-300 bg-white dark:bg-gray-800 hover:bg-sky-50 dark:hover:bg-gray-700';
          case 'ruby':
            return 'border-red-400 dark:border-red-700 text-red-700 dark:text-red-300 bg-white dark:bg-gray-800 hover:bg-red-50 dark:hover:bg-gray-700';
          case 'emerald':
            return 'border-emerald-400 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300 bg-white dark:bg-gray-800 hover:bg-emerald-50 dark:hover:bg-gray-700';
          case 'sapphire':
            return 'border-blue-400 dark:border-blue-700 text-blue-700 dark:text-blue-300 bg-white dark:bg-gray-800 hover:bg-blue-50 dark:hover:bg-gray-700';
          case 'amethyst':
            return 'border-fuchsia-400 dark:border-fuchsia-700 text-fuchsia-700 dark:text-fuchsia-300 bg-white dark:bg-gray-800 hover:bg-fuchsia-50 dark:hover:bg-gray-700';
          case 'amber':
            return 'border-amber-400 dark:border-amber-700 text-amber-700 dark:text-amber-300 bg-white dark:bg-gray-800 hover:bg-amber-50 dark:hover:bg-gray-700';
          case 'burgundy':
            return 'border-red-500 dark:border-red-700 text-red-800 dark:text-red-300 bg-white dark:bg-gray-800 hover:bg-red-50 dark:hover:bg-gray-700';
          case 'navy':
            return 'border-blue-500 dark:border-blue-700 text-blue-800 dark:text-blue-300 bg-white dark:bg-gray-800 hover:bg-blue-50 dark:hover:bg-gray-700';
          case 'forest':
            return 'border-green-500 dark:border-green-700 text-green-800 dark:text-green-300 bg-white dark:bg-gray-800 hover:bg-green-50 dark:hover:bg-gray-700';
          case 'gold':
            return 'border-yellow-400 dark:border-yellow-600 text-yellow-700 dark:text-yellow-300 bg-white dark:bg-gray-800 hover:bg-yellow-50 dark:hover:bg-gray-700';
          case 'silver':
            return 'border-gray-400 dark:border-gray-600 text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700';
          default:
            // Default to a nice, soft pink if theme is not recognized
            return 'border-pink-300 dark:border-pink-700 text-pink-700 dark:text-pink-300 bg-white dark:bg-gray-800 hover:bg-pink-50 dark:hover:bg-gray-700';
        }
        
      case 'danger':
        return 'border-transparent text-white bg-red-500 hover:bg-red-600 focus:ring-2 focus:ring-offset-2 focus:ring-red-400';
        
      default:
        return themeClasses.primaryButton;
    }
  };
  
  // If customClasses is provided, use those instead of variant-based styling
  if (customClasses) {
    return (
      <button
        className={`
          ${baseStyles} 
          ${sizeStyles[size]} 
          ${widthClass} 
          ${customClasses}
          ${className}
        `}
        disabled={disabled || loading}
        {...rest}
      >
        {loading ? (
          <>
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Loading...
          </>
        ) : (
          <>
            {leftIcon && <span className="mr-2">{leftIcon}</span>}
            {children}
            {rightIcon && <span className="ml-2">{rightIcon}</span>}
          </>
        )}
      </button>
    );
  }

  return (
    <button
      className={`
        ${baseStyles} 
        ${getVariantStyles()} 
        ${sizeStyles[size]} 
        ${widthClass} 
        ${className}
      `}
      disabled={disabled || loading}
      {...rest}
    >
      {loading ? (
        <>
          <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Loading...
        </>
      ) : (
        <>
          {leftIcon && <span className="mr-2">{leftIcon}</span>}
          {children}
          {rightIcon && <span className="ml-2">{rightIcon}</span>}
        </>
      )}
    </button>
  );
};

export default Button;